/**
 * Time of day for the world's sky, sun and window lights. Pure (no three.js), so the maths is
 * unit tested. The default is the viewer's local time; `?tod=night`, `?tod=day` or `?tod=14.5`
 * (hours) overrides it for screenshots and tests.
 */

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export interface SunState {
  /** Hours 0..24. */
  hours: number;
  /** Unit vector toward the sun (day) or the moon (night): +X east, +Y up, -Z north. */
  direction: [number, number, number];
  /** Sine of the sun's elevation (negative below the horizon). */
  elevation: number;
  /** 1 in full daylight, 0 at night (dusk and dawn in between). */
  daylight: number;
  /** 1 when windows and lamps should glow, 0 in daylight. */
  night: number;
  /** Warmth of the light: 0 at noon, 1 at sunrise and sunset. */
  warmth: number;
}

/** Hours from a `tod` query value or the given local date. */
export function hoursFor(search: string, now: Date = new Date()): number {
  const m = /[?&]tod=([^&]+)/.exec(search);
  if (m) {
    const v = decodeURIComponent(m[1]!);
    if (v === "day") return 12.5;
    if (v === "night") return 23;
    if (v === "dusk") return 17.6;
    const n = Number(v);
    if (Number.isFinite(n)) return ((n % 24) + 24) % 24;
  }
  return now.getHours() + now.getMinutes() / 60;
}

export function sunAt(hours: number): SunState {
  // Sunrise at 6:00, sunset at 18:00; the sun peaks at 62 degrees.
  const phase = ((hours - 6) / 12) * Math.PI; // 0 at sunrise, pi at sunset
  const el = Math.sin(phase) * 0.88; // sin(elevation) for daytime
  const sinE =
    hours >= 6 && hours <= 18 ? el : -Math.abs(Math.sin(((hours - 18) / 12) * Math.PI)) * 0.6;
  const cosE = Math.sqrt(Math.max(0, 1 - sinE * sinE));
  const az = Math.cos(phase); // east (+1) at sunrise, west (-1) at sunset
  const south = 0.42;
  let x = az * cosE;
  let z = south * cosE;
  let y = sinE;
  if (sinE < 0) {
    // The moon: high and to the south-west, whatever the hour.
    x = -0.45;
    y = 0.75;
    z = 0.48;
  }
  const len = Math.hypot(x, y, z) || 1;
  const daylight = smooth(-0.04, 0.3, sinE);
  return {
    hours,
    direction: [x / len, y / len, z / len],
    elevation: sinE,
    daylight,
    night: 1 - smooth(-0.14, 0.1, sinE),
    warmth: sinE > -0.1 ? 1 - smooth(0.05, 0.55, sinE) : 0,
  };
}

/**
 * Raises or lowers the sun toward `sinElevation` (the sun inside the day HDRI) by `weight`
 * (0 keeps the clock's sun, 1 matches the image), keeping its azimuth. Only the direction
 * changes: daylight, warmth and night still follow the clock.
 */
export function withElevation(sun: SunState, sinElevation: number, weight: number): SunState {
  if (weight <= 0 || sun.elevation <= 0) return sun;
  const y = sun.elevation + (sinElevation - sun.elevation) * Math.min(1, weight);
  const [dx, , dz] = sun.direction;
  const flat = Math.hypot(dx, dz) || 1;
  const k = Math.sqrt(Math.max(0, 1 - y * y)) / flat;
  return { ...sun, direction: [dx * k, y, dz * k] };
}

/** How strongly the day image's sun elevation applies: full at noon, none once the light turns warm. */
export function hdriSunWeight(sun: SunState): number {
  return Math.min(1, Math.max(0, 1 - sun.warmth / 0.55)) * sun.daylight;
}

// ---------------------------------------------------------------- the user's time control

/** The user's choice: follow the viewer's clock, or hold the sky at an hour (0..24). */
export type TimeSetting = "auto" | number;

/** Named presets of the HUD control; each maps to an hour. */
export const TIME_PRESETS = { day: 12.5, dusk: 17.6, night: 23 } as const;
export type TimePreset = keyof typeof TIME_PRESETS;

export function normalizeHours(h: number): number {
  return ((h % 24) + 24) % 24;
}

/** The hour a setting means right now (`now` only matters for `auto`). */
export function hoursForSetting(setting: TimeSetting, now: Date = new Date()): number {
  return setting === "auto" ? now.getHours() + now.getMinutes() / 60 : normalizeHours(setting);
}

/** Which preset an hour is (nearly) at, for highlighting the HUD buttons; null between them. */
export function presetOf(setting: TimeSetting): TimePreset | null {
  if (setting === "auto") return null;
  for (const k of Object.keys(TIME_PRESETS) as TimePreset[]) {
    if (Math.abs(normalizeHours(setting) - TIME_PRESETS[k]) < 0.05) return k;
  }
  return null;
}

/** `HH:MM` for an hour value. */
export function formatHours(h: number): string {
  const total = Math.round(normalizeHours(h) * 60) % (24 * 60);
  const mm = String(total % 60).padStart(2, "0");
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${mm}`;
}

/** Reads a stored or server value: `auto` or a finite number, anything else is rejected. */
export function parseTimeSetting(v: unknown): TimeSetting | null {
  if (v === "auto") return "auto";
  if (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 24) return v;
  return null;
}

/** A word-start match that also works for Turkish letters (`\b` is ASCII-only). */
const word = (pattern: string): RegExp => new RegExp(`(?<![\\p{L}])(?:${pattern})`, "u");

const SET_WORDS = word(
  "set|make|switch|change|turn|put|yap|ayarla|geçir|çevir|getir|olsun|düzenle|dön",
);
const WORLD_WORDS = word("world|city|sky|scene|dünya|şehir|gökyüzü|gok");
const NIGHT = word("night|midnight|gece|karanlık");
const DAY = word("day|daytime|noon|midday|gündüz|öğle|öğlen");
const DUSK = word("dusk|sunset|evening|twilight|akşam|gün batım|alacakaranlık");
const AUTO = word(
  "auto|automatic|real[- ]?time|real clock|current time|otomatik|gerçek saat|şu anki saat",
);
const PRONOUN = word("it|this|onu|bunu|her şeyi");
const TIME_CUE = word("time|saat|hour|clock");

/**
 * Understands a short spoken or typed command to change the sky ("set the world to night",
 * "dünyayı geceye çevir", "make it daytime", "saati 21:30 yap"). Returns the new setting, or null
 * when the text is not such a command, so ordinary chat goes to the assistant untouched. Works
 * offline: it needs no server.
 */
export function parseTimeIntent(text: string): TimeSetting | null {
  const s = text.trim().toLowerCase();
  if (s.length === 0 || s.length > 80) return null;
  const night = NIGHT.test(s);
  const day = DAY.test(s);
  const dusk = DUSK.test(s);
  const auto = AUTO.test(s);
  const kinds = [night, day, dusk, auto].filter(Boolean).length;
  if (kinds > 1) return null;
  if (!SET_WORDS.test(s) && !WORLD_WORDS.test(s)) return null;
  if (kinds === 0) {
    // "set the time to 21:30", "saati 9 yap", "sky at 6 pm".
    if (!TIME_CUE.test(s) && !WORLD_WORDS.test(s)) return null;
    const m = /(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?/.exec(s);
    if (!m) return null;
    let h = Number(m[1]);
    const min = m[2] ? Number(m[2]) : 0;
    if (m[3] === "pm" && h < 12) h += 12;
    if (m[3] === "am" && h === 12) h = 0;
    return h <= 24 && min < 60 ? normalizeHours(h + min / 60) : null;
  }
  // A named time of day needs a cue that this is about the sky ("world", "time", "it") or to be
  // a two-word command, so "make a day plan" or "set up automation" reach the assistant.
  const words = s.split(/\s+/).length;
  if (!(WORLD_WORDS.test(s) || TIME_CUE.test(s) || PRONOUN.test(s) || words <= 2)) return null;
  if (night) return TIME_PRESETS.night;
  if (day) return TIME_PRESETS.day;
  if (dusk) return TIME_PRESETS.dusk;
  return "auto";
}
